import React, { useEffect, useState, useRef, Children, Component } from 'react';
import { motion, AnimatePresence, useInView, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MailIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { SEO } from '../components/SEO';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20
    }
  }
};
const values = [{
  title: 'Simplicity First',
  description: 'We cut through the noise to bring you tools that are easy to understand and use immediately.',
  icon: '✨'
}, {
  title: 'For Everyone',
  description: 'Whether you are a student, parent, or professional, AI should be accessible to you.',
  icon: '🌍'
}, {
  title: 'Curated Quality',
  description: 'We personally test and verify every tool before it makes it to our platform.',
  icon: '🎯'
}, {
  title: 'Always Free',
  description: 'Discovering the right tools should not cost you anything. Our platform is free to use.',
  icon: '💝'
}];
const steps = [{
  number: '01',
  title: 'Discover',
  description: 'Search by task or browse categories tailored to your specific needs.'
}, {
  number: '02',
  title: 'Try',
  description: 'Read our concise summaries and click through to test the tools yourself.'
}, {
  number: '03',
  title: 'Integrate',
  description: 'Make the best tools part of your daily workflow and save hours of time.'
}];
const personas = [{
  id: 'beginner',
  icon: '🌱',
  title: 'The Curious Beginner',
  teaser: 'Heard about AI but does not know where to begin.',
  fullText: '4ALL is for the person who has heard about AI but does not know where to begin. We guide you step-by-step.'
}, {
  id: 'professional',
  icon: '💼',
  title: 'The Busy Professional',
  teaser: 'Wants to work smarter but does not have hours to spend.',
  fullText: 'It is for the professional who wants to work smarter but does not have hours to spend researching tools. Get straight to what works.'
}, {
  id: 'learner',
  icon: '📚',
  title: 'The Lifelong Learner',
  teaser: 'Curious, busy, felt like tech was not designed for them.',
  fullText: 'It is for the learner who is curious, the operator who is busy, and anyone who has ever felt like the tech world was not designed with them in mind.'
}, {
  id: 'everyone',
  icon: '🤝',
  title: 'Everyone Else',
  teaser: 'You do not need a technical background...',
  fullText: 'You do not need a technical background. You do not need to know the terminology. You just need to know what you want to get done — and we will help you find the right tool, understand how it works and take the next step with confidence.'
}];
const sections = [{
  id: 'hero',
  label: 'Intro'
}, {
  id: 'mission',
  label: 'Mission'
}, {
  id: 'who',
  label: "Who It's For"
}, {
  id: 'what',
  label: 'What We Do'
}, {
  id: 'values',
  label: 'Values'
}, {
  id: 'how',
  label: 'How It Works'
}, {
  id: 'stats',
  label: 'Impact'
}, {
  id: 'note',
  label: 'Our Promise'
}];
// Animated Counter Component
function AnimatedCounter({
  from,
  to,
  duration = 1.5,
  suffix = ''
}: {
  from: number;
  to: number;
  duration?: number;
  suffix?: string;
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const inView = useInView(nodeRef, {
    once: true,
    margin: '-50px'
  });
  const [count, setCount] = useState(from);
  useEffect(() => {
    if (inView) {
      let startTimestamp: number | null = null;
      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / (duration * 1000), 1);
        // easeOutQuart
        const easeProgress = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeProgress * (to - from) + from));
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    }
  }, [inView, from, to, duration]);
  return <div ref={nodeRef} data-id="element-169">
      {count}
      {suffix}
    </div>;
}
// Flip Card Component
function FlipCard({
  value
}: {
  value: (typeof values)[0];
}) {
  const [isFlipped, setIsFlipped] = useState(false);
  return <div className="relative h-64 w-full perspective-1000 cursor-pointer group" onMouseEnter={() => setIsFlipped(true)} onMouseLeave={() => setIsFlipped(false)} onClick={() => setIsFlipped(!isFlipped)} data-id="element-170">
      <motion.div className="w-full h-full relative preserve-3d transition-all duration-500" animate={{
      rotateY: isFlipped ? 180 : 0
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20
    }} data-id="element-171">
        {/* Front */}
        <div className="absolute w-full h-full backface-hidden bg-grey-100 rounded-3xl p-8 flex flex-col items-center justify-center text-center shadow-sm group-hover:shadow-md transition-shadow" data-id="element-172">
          <div className="text-5xl mb-4" data-id="element-173">{value.icon}</div>
          <h3 className="text-xl font-bold text-black" data-id="element-174">{value.title}</h3>
        </div>

        {/* Back */}
        <div className="absolute w-full h-full backface-hidden bg-black text-white rounded-3xl p-8 flex items-center justify-center text-center shadow-xl" style={{
        transform: 'rotateY(180deg)'
      }} data-id="element-175">
          <p className="text-base leading-relaxed" data-id="element-176">{value.description}</p>
        </div>
      </motion.div>
    </div>;
}
export function AboutPage() {
  const [activeSection, setActiveSection] = useState('hero');
  const [expandedPersona, setExpandedPersona] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  // Intersection Observer for sticky nav
  useEffect(() => {
    const observers = sections.map(section => {
      const element = document.getElementById(section.id);
      if (!element) return null;
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(section.id);
          }
        });
      }, {
        rootMargin: '-20% 0px -60% 0px'
      });
      observer.observe(element);
      return observer;
    });
    return () => {
      observers.forEach(obs => obs?.disconnect());
    };
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // header offset
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  // Auto-advance stepper
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep(prev => (prev + 1) % steps.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);
  return <div className="min-h-screen bg-white relative" data-id="element-177">
      <SEO title="About 4ALL AI — Making AI Accessible to Everyone" description="Learn about 4ALL AI, the free AI tool directory on a mission to democratise AI access. We curate, explain and organise AI tools for students, professionals and everyday users." keywords="about 4ALL AI, AI accessibility, AI for everyone, AI tool curation, AI education, AI literacy, democratise AI, AI discovery platform" canonicalPath="/about" data-id="element-178" />
      <Header data-id="element-179" />

      {/* Sticky Navigation - Mobile (Top) */}
      <div className="md:hidden sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-grey-200 py-3 px-4 overflow-x-auto whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-180">
        <div className="flex gap-2" data-id="element-181">
          {sections.map(section => <button key={section.id} onClick={() => scrollToSection(section.id)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeSection === section.id ? 'bg-black text-white' : 'bg-grey-100 text-grey-600'}`} data-id="element-182">
              {section.label}
            </button>)}
        </div>
      </div>

      <div className="max-w-[90rem] mx-auto flex" data-id="element-183">
        {/* Sticky Navigation - Desktop (Left) */}
        <aside className="hidden md:block w-64 shrink-0 sticky top-24 h-[calc(100vh-6rem)] overflow-y-auto pl-8 py-12" data-id="element-184">
          <nav className="flex flex-col gap-4" data-id="element-185">
            {sections.map(section => <button key={section.id} onClick={() => scrollToSection(section.id)} className={`text-left text-sm font-medium transition-all duration-300 flex items-center gap-3 ${activeSection === section.id ? 'text-black translate-x-2' : 'text-grey-400 hover:text-grey-600'}`} data-id="element-186">
                <span className={`w-1.5 h-1.5 rounded-full transition-colors ${activeSection === section.id ? 'bg-black' : 'bg-transparent'}`} data-id="element-187" />
                {section.label}
              </button>)}
          </nav>
        </aside>

        <main className="flex-1 w-full pb-24" data-id="element-188">
          {/* Hero Section */}
          <motion.section id="hero" className="max-w-3xl mx-auto px-6 pt-16 md:pt-24 pb-16" variants={containerVariants} initial="hidden" animate="visible" data-id="element-189">
            <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl font-bold text-black mb-12 tracking-tight text-center" data-id="element-190">
              About 4ALL
            </motion.h1>
            <motion.div variants={itemVariants} className="relative mb-16" data-id="element-191">
              <p className="text-3xl md:text-4xl text-black font-medium leading-tight text-center" data-id="element-192">
                We believe AI should work for{' '}
                <span className="relative inline-block" data-id="element-193">
                  everyone.
                  <motion.span className="absolute -bottom-2 left-0 w-full h-3 bg-black/10 -z-10 rounded-sm" initial={{
                  width: 0
                }} animate={{
                  width: '100%'
                }} transition={{
                  delay: 0.8,
                  duration: 0.6,
                  ease: 'easeOut'
                }} data-id="element-194" />
                </span>
                <br data-id="element-195" />
                <span className="text-grey-500 text-2xl md:text-3xl mt-2 block" data-id="element-196">
                  Not just the technical. Not just the privileged. Everyone.
                </span>
              </p>
            </motion.div>

            <div className="space-y-8 text-lg md:text-xl text-grey-700 leading-relaxed" data-id="element-197">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} data-id="element-198">
                The world is changing fast. AI is reshaping how we work, learn,
                create and solve problems — and the people who understand how to
                use it are gaining a real and growing advantage.
              </motion.p>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} data-id="element-199">
                But most of the information out there assumes you already know
                the language. It assumes you have time to dig through threads,
                watch hours of tutorials, or sort through hundreds of tools with
                no guidance on where to start.
              </motion.p>
              <motion.p className="font-medium text-black text-2xl" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.5
            }} data-id="element-200">
                4ALL exists because that gap is not acceptable.
              </motion.p>
            </div>
          </motion.section>

          {/* Our Mission */}
          <motion.section id="mission" className="max-w-3xl mx-auto px-6 py-12 sm:py-20" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} data-id="element-201">
            <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-black mb-12 flex items-center gap-4" data-id="element-202">
              Our Mission
            </motion.h2>

            <motion.div variants={itemVariants} className="relative mb-12" data-id="element-203">
              <span className="absolute -top-8 sm:-top-10 -left-2 sm:-left-6 text-6xl sm:text-8xl text-grey-200 font-serif leading-none select-none" data-id="element-204">
                "
              </span>
              <p className="font-medium text-black text-2xl md:text-3xl leading-snug relative z-10 pl-6 border-l-4 border-black" data-id="element-205">
                To democratize access to AI knowledge — making it simple, honest
                and genuinely useful for everyday people, regardless of
                background, budget or technical ability.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-8 text-lg md:text-xl text-grey-700 leading-relaxed" data-id="element-206">
              <p data-id="element-207">
                We do not believe the benefits of artificial intelligence should
                be reserved for engineers, enterprise teams or early adopters
                with the luxury of time. We believe a parent, a student, a small
                business owner, a teacher and a retiree all deserve the same
                clarity and confidence when it comes to understanding and using
                AI.
              </p>
              <motion.p className="font-medium text-black bg-grey-100 p-6 rounded-2xl" whileHover={{
              scale: 1.02
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 10
            }} data-id="element-208">
                4ALL is built on one idea: that when information is made
                accessible, people move forward.
              </motion.p>
            </motion.div>
          </motion.section>

          {/* Who This Is For */}
          <motion.section id="who" className="max-w-4xl mx-auto px-6 py-12 sm:py-20" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} data-id="element-209">
            <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-black mb-12 text-center" data-id="element-210">
              Who This Is For
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-211">
              {personas.map(persona => <motion.div key={persona.id} variants={itemVariants} layout onClick={() => setExpandedPersona(expandedPersona === persona.id ? null : persona.id)} className={`cursor-pointer rounded-3xl p-8 transition-all duration-300 ${expandedPersona === persona.id ? 'bg-black text-white shadow-2xl scale-[1.02] z-10' : 'bg-grey-100 text-black hover:bg-grey-200 hover:scale-[1.01]'}`} data-id="element-212">
                  <motion.div layout className="flex items-center gap-4 mb-4" data-id="element-213">
                    <span className="text-3xl" data-id="element-214">{persona.icon}</span>
                    <h3 className="text-xl font-bold" data-id="element-215">{persona.title}</h3>
                  </motion.div>

                  <AnimatePresence mode="wait" data-id="element-216">
                    {expandedPersona === persona.id ? <motion.p key="full" initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2
                }} className="text-grey-300 leading-relaxed" data-id="element-217">
                        {persona.fullText}
                      </motion.p> : <motion.p key="teaser" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="text-grey-600 leading-relaxed" data-id="element-218">
                        {persona.teaser}
                      </motion.p>}
                  </AnimatePresence>
                </motion.div>)}
            </div>
          </motion.section>

          {/* What We Do */}
          <motion.section id="what" className="max-w-3xl mx-auto px-6 py-12 sm:py-20" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} data-id="element-219">
            <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-black mb-8" data-id="element-220">
              What We Do
            </motion.h2>
            <motion.div variants={itemVariants} className="space-y-12 text-lg md:text-xl text-grey-700 leading-relaxed" data-id="element-221">
              <p data-id="element-222">
                We curate, explain and organise AI tools and use cases so you do
                not have to do the hard work of figuring it out alone. Every
                recommendation is chosen with real people in mind — not power
                users, not developers, not insiders. People with real lives and
                real things to get done.
              </p>

              <motion.div className="text-center py-8" initial={{
              scale: 0.9,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              type: 'spring',
              stiffness: 100,
              damping: 20
            }} data-id="element-223">
                <p className="font-bold text-black text-3xl md:text-4xl leading-tight" data-id="element-224">
                  We translate complexity into clarity. <br data-id="element-225" />
                  <span className="text-grey-400" data-id="element-226">
                    We replace overwhelm with momentum.
                  </span>
                </p>
              </motion.div>

              <p className="text-center font-medium" data-id="element-227">
                And we are just getting started.
              </p>
            </motion.div>
          </motion.section>

          {/* Values/Pillars */}
          <section id="values" className="max-w-5xl mx-auto px-6 py-12 sm:py-20" data-id="element-228">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-229">
              {values.map((value, index) => <motion.div key={index} variants={itemVariants} data-id="element-230">
                  <FlipCard value={value} data-id="element-231" />
                </motion.div>)}
            </motion.div>
          </section>

          {/* How It Works */}
          <section id="how" className="max-w-5xl mx-auto px-6 py-14 sm:py-24" data-id="element-232">
            <div className="bg-black text-white rounded-2xl sm:rounded-[3rem] p-8 sm:p-10 md:p-16 relative overflow-hidden" data-id="element-233">
              {/* Decorative background element */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-234" />

              <div className="text-center mb-12 sm:mb-16 relative z-10" data-id="element-235">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-236">
                  How It Works
                </h2>
                <p className="text-grey-400" data-id="element-237">
                  Your journey to finding the perfect AI tool
                </p>
              </div>

              <div className="relative z-10 max-w-3xl mx-auto" data-id="element-238">
                {/* Progress Bar */}
                <div className="flex justify-between mb-12 relative" data-id="element-239">
                  <div className="absolute top-1/2 left-0 w-full h-1 bg-grey-800 -translate-y-1/2 rounded-full" data-id="element-240" />
                  <motion.div className="absolute top-1/2 left-0 h-1 bg-white -translate-y-1/2 rounded-full" initial={{
                  width: '0%'
                }} animate={{
                  width: `${activeStep / (steps.length - 1) * 100}%`
                }} transition={{
                  duration: 0.5,
                  ease: 'easeInOut'
                }} data-id="element-241" />

                  {steps.map((step, index) => <button key={index} onClick={() => setActiveStep(index)} className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors duration-300 ${index <= activeStep ? 'bg-white text-black' : 'bg-grey-800 text-grey-500'}`} data-id="element-242">
                      {index + 1}
                    </button>)}
                </div>

                {/* Active Step Content */}
                <div className="h-48 flex items-center justify-center text-center" data-id="element-243">
                  <AnimatePresence mode="wait" data-id="element-244">
                    <motion.div key={activeStep} initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -20
                  }} transition={{
                    duration: 0.3
                  }} className="max-w-xl" data-id="element-245">
                      <div className="text-4xl sm:text-6xl font-black text-grey-800 mb-4 sm:mb-6 select-none" data-id="element-246">
                        {steps[activeStep].number}
                      </div>
                      <h3 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4" data-id="element-247">
                        {steps[activeStep].title}
                      </h3>
                      <p className="text-lg sm:text-xl text-grey-400 leading-relaxed" data-id="element-248">
                        {steps[activeStep].description}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </section>

          {/* Stats/Impact */}
          <section id="stats" className="max-w-4xl mx-auto px-6 py-12 sm:py-20" data-id="element-249">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="flex flex-wrap justify-center gap-12 md:gap-24 text-center" data-id="element-250">
              <motion.div variants={itemVariants} data-id="element-251">
                <div className="text-5xl md:text-7xl font-black text-black mb-2 flex justify-center" data-id="element-252">
                  <AnimatedCounter from={0} to={50} suffix="+" data-id="element-253" />
                </div>
                <div className="text-grey-500 font-medium uppercase tracking-wider text-sm" data-id="element-254">
                  Tools Curated
                </div>
              </motion.div>
              <motion.div variants={itemVariants} data-id="element-255">
                <div className="text-5xl md:text-7xl font-black text-black mb-2 flex justify-center" data-id="element-256">
                  <AnimatedCounter from={0} to={6} data-id="element-257" />
                </div>
                <div className="text-grey-500 font-medium uppercase tracking-wider text-sm" data-id="element-258">
                  Categories
                </div>
              </motion.div>
              <motion.div variants={itemVariants} data-id="element-259">
                <div className="text-5xl md:text-7xl font-black text-black mb-2" data-id="element-260">
                  Weekly
                </div>
                <div className="text-grey-500 font-medium uppercase tracking-wider text-sm" data-id="element-261">
                  Updates
                </div>
              </motion.div>
            </motion.div>
          </section>

          {/* A Note on How We Work */}
          <section id="note" className="max-w-4xl mx-auto px-6 py-12 sm:py-20" data-id="element-262">
            <motion.div className="bg-grey-100 rounded-3xl p-8 sm:p-10 md:p-16 border-l-4 border-black shadow-sm" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={containerVariants} data-id="element-263">
              <motion.h2 variants={itemVariants} className="text-2xl sm:text-3xl font-bold text-black mb-6" data-id="element-264">
                A Note on How We Work
              </motion.h2>
              <motion.p variants={itemVariants} className="text-lg md:text-xl text-grey-700 leading-relaxed" data-id="element-265">
                <span className="font-medium text-black" data-id="element-266">4ALL</span> is
                independent. We are not paid to feature tools. We do not rank
                things based on sponsorship. Our goal is to give you an honest,
                useful and well-organised view of what is out there — so you can
                make decisions that are right for you, not right for an
                advertiser.
              </motion.p>
            </motion.div>
          </section>

          {/* Join the Community / CTA */}
          <motion.section className="max-w-3xl mx-auto px-6 py-14 sm:py-24 text-center" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} data-id="element-267">
            <motion.div variants={itemVariants} className="space-y-6 text-xl md:text-2xl text-grey-700 mb-12 sm:mb-16" data-id="element-268">
              <p className="font-medium text-black" data-id="element-269">
                AI literacy is not a luxury. It is a life skill - and it belongs
                to all of us.
              </p>
              <p data-id="element-270">
                Whether you are exploring for the first time or building out
                your personal AI stack, you are in the right place.
              </p>
              <p className="font-bold text-black text-3xl md:text-4xl mt-12" data-id="element-271">
                Welcome to 4ALL. This was built for you.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="relative inline-block" data-id="element-272">
              {/* Pulsing glow effect */}
              <motion.div className="absolute -inset-2 bg-black/20 rounded-full blur-md" animate={{
              scale: [1, 1.1, 1],
              opacity: [0.5, 0.8, 0.5]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-273" />
              <Link to="/" className="relative block" data-id="element-274">
                <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="bg-black text-white px-8 sm:px-12 py-4 sm:py-6 rounded-full text-lg sm:text-xl font-bold hover:bg-grey-800 transition-colors shadow-xl" data-id="element-275">
                  Start Exploring
                </motion.button>
              </Link>
            </motion.div>
          </motion.section>
        </main>
      </div>

      {/* Newsletter Signup */}
      <section className="w-full bg-grey-50 border-t border-grey-200" data-id="element-276">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center" data-id="element-277">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-black" data-id="element-278">
            <MailIcon className="h-5 w-5 text-white" data-id="element-279" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-grey-900 mb-2" data-id="element-280">
            Be the first to know
          </h3>
          <p className="text-sm text-grey-500 mb-6 max-w-sm mx-auto" data-id="element-281">
            Get notified when we add new AI tools to the directory. No spam,
            just the latest discoveries.
          </p>

          {newsletterSubmitted ? <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center gap-2" data-id="element-282">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black" data-id="element-283">
                <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} data-id="element-284">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-285" />
                </svg>
              </div>
              <p className="text-sm font-medium text-grey-900" data-id="element-286">
                You're on the list!
              </p>
              <p className="text-xs text-grey-400" data-id="element-287">
                We'll keep you posted on the latest tools.
              </p>
            </motion.div> : <button onClick={() => setNewsletterSubmitted(true)} className="rounded-full bg-black px-8 py-3 text-sm font-medium text-white hover:bg-grey-800 transition-colors" data-id="element-288">
              Subscribe to updates
            </button>}
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-grey-200" data-id="element-289">
        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-290">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-grey-500" data-id="element-291">
            <p className="text-center sm:text-left" data-id="element-292">
              © 2026 4ALL AI | All Rights Reserved by Skeuomorphic xyz
            </p>
            <div className="flex items-center gap-6" data-id="element-293">
              <Link to="/privacy" className="hover:text-black transition-colors" data-id="element-294">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-black transition-colors" data-id="element-295">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS for 3D flip effect */}
      <style dangerouslySetInnerHTML={{
      __html: `
        .perspective-1000 { perspective: 1000px; }
        .preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
      `
    }} data-id="element-296" />
    </div>;
}